import React from 'react';
import { Database } from 'lucide-react';
import HowToRead from './HowToRead';

export type TypedMetricStatus =
  | 'MEASURED'
  | 'MODELED'
  | 'ESTIMATED'
  | 'WITHHELD'
  | 'SUPPRESSED'
  | string;

export interface TypedMetricEntry {
  key: string;
  label: string;
  window?: string;
  basis: string;
  unit: string;
  value: number | null;
  display: string;
  status: TypedMetricStatus;
  /** Temporal-recency axis (REAL_TIME / DAILY / HOURLY / WINDOWED / MODELED / STATIC_REF).
   *  Distinct from status (data-quality axis); rendered as a small chip so a card with
   *  mixed-tier metrics doesn't hide the mismatch under one blanket LIVE_VERIFIED badge. */
  freshness_tier?: string;
  provenance?: string;
}

export interface TypedMetricRegistryShape {
  version: string;
  generated_at: string;
  metrics: Record<string, TypedMetricEntry>;
}

interface Props {
  registry?: TypedMetricRegistryShape | null;
  className?: string;
}

// Per-status colour + border. Kept together so a reader learns the mapping once and reads the
// same colour everywhere the chip renders.
const STATUS_TONE: Record<string, string> = {
  MEASURED: 'bg-emerald-900/30 border-emerald-800/60 text-emerald-300',
  MODELED: 'bg-indigo-900/30 border-indigo-800/60 text-indigo-300',
  ESTIMATED: 'bg-amber-900/25 border-amber-800/60 text-amber-300',
  // INDICATIVE — borderline (Kupiec YELLOW) or short-sample. Amber-red.
  INDICATIVE: 'bg-red-900/30 border-red-800/60 text-red-300',
  // DATA_SUSPECT — the backtest DECISIVELY rejected the model with power (Basel RED zone). Strongest
  // warning tone so it is not read as a mere low-power "indicative".
  DATA_SUSPECT: 'bg-red-950/50 border-red-600 text-red-200',
  WITHHELD: 'bg-slate-800/50 border-slate-700 text-slate-300',
  SUPPRESSED: 'bg-slate-800/50 border-slate-700 text-slate-300',
};

const STATUS_HELP: Record<string, string> = {
  INDICATIVE: 'Computed, but the reliability backtest is borderline (Kupiec YELLOW) or the sample is short — indicative-only, not measurement-grade.',
  DATA_SUSPECT: 'The Kupiec VaR-coverage backtest DECISIVELY REJECTED the tail model with power (Basel RED zone) — the model under-covers; the number is shown for continuity but is suspect, not a measurement.',
};

const StatusChip: React.FC<{ status: TypedMetricStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center rounded border px-1.5 py-[1px] text-[10px] font-mono ${STATUS_TONE[status] ?? 'bg-slate-800/40 border-slate-700 text-slate-300'}`}
    title={STATUS_HELP[status] ?? `Data-quality tier: ${status}`}
  >
    {status}
  </span>
);

/**
 * Renders the typed_metric_registry MVP: the ONE authoritative object per (key, window) tuple.
 * Every value shown here carries its own status chip so a reader can tell a MEASURED number
 * from an ESTIMATED one at a glance. Additional metrics land in this same table as the
 * aggregator wires them into BuildTypedMetricRegistry — no further UI change needed.
 */
const TypedMetricRegistryPanel: React.FC<Props> = ({ registry, className }) => {
  if (!registry || !registry.metrics || Object.keys(registry.metrics).length === 0) return null;
  const entries = Object.values(registry.metrics).sort((a, b) => a.key.localeCompare(b.key));
  return (
    <div className={`rounded-md bg-[color:var(--c-panel-black)] border border-[color:rgb(var(--c-ink-rgb)/0.06)] p-4 ${className ?? ''}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
        <Database size={14} className="text-cmd-textDim" />
        <span className="text-sm font-semibold text-cmd-text">Typed metric registry</span>
        <span className="text-[10px] font-mono text-cmd-textDim">({registry.version} · {entries.length} metrics)</span>
      </div>
      <div className="mb-3">
        <HowToRead explainKey="typed_metric_registry" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1040px] text-[12px] font-mono">
          <thead>
            <tr className="text-cmd-textDim">
              <th className="text-left font-normal py-1">Key</th>
              <th className="text-left font-normal py-1">Label</th>
              <th className="text-right font-normal py-1">Value</th>
              <th className="text-left font-normal py-1 pl-3">Status</th>
              <th className="text-left font-normal py-1 pl-3">Freshness</th>
              <th className="text-left font-normal py-1 pl-3">Basis</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((m) => (
              <tr key={m.key} className="border-t border-[color:rgb(var(--c-ink-rgb)/0.05)]" title={m.provenance ?? ''}>
                <td className="py-1 text-cmd-text">{m.key}</td>
                <td className="py-1 text-cmd-textDim">{m.label}</td>
                <td className={`py-1 text-right ${m.value == null ? 'text-cmd-textDim' : 'text-cmd-text'}`}>{m.display}</td>
                <td className="py-1 pl-3"><StatusChip status={m.status} /></td>
                <td className="py-1 pl-3 text-[10px] text-cmd-textDim" title="Temporal-recency axis (REAL_TIME / DAILY / HOURLY / WINDOWED / MODELED / STATIC_REF) — distinct from Status.">
                  {m.freshness_tier ?? '—'}
                </td>
                <td className="py-1 pl-3 text-[10px] text-cmd-textDim">{m.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TypedMetricRegistryPanel;
